import { useState } from 'react'
import { Link } from 'react-router'
import { authApiService } from '../services/authApiService'
import { ApiError } from '../services/BaseApiService'
import { useTurnstileConfig, TurnstileWidget } from '../components/Turnstile'
import { colors, fontSize, radius, shadow, spacing, fontFamily, componentHeight, transition } from '../styles/theme'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const turnstile = useTurnstileConfig()
  const [turnstileToken, setTurnstileToken] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (turnstile.enabled && !turnstileToken) { setError('Please complete the verification challenge.'); return }
    setLoading(true)
    try {
      await authApiService.forgotPassword(email, turnstileToken)
      setSuccess(true)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Request failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: colors.surfaceTinted, padding: '24px 16px', fontFamily: fontFamily.sans,
    }}>
      {/* Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
            <path d="M4 3h8l4 4v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" fill="#fff" opacity={0.9} />
            <path d="M12 3l4 4h-4V3z" fill="#fff" opacity={0.6} />
            <path d="M7 9h6M7 12h6M7 15h4" stroke="#0F9D92" strokeWidth={1.2} strokeLinecap="round" />
          </svg>
        </div>
        <span style={{ fontSize: '22px', fontWeight: 600, color: colors.text, letterSpacing: '-0.02em' }}>
          Doc<span style={{ color: colors.primary }}>morph</span>ia
        </span>
      </div>

      <div style={{
        background: colors.surface, borderRadius: radius.lg, border: `1px solid ${colors.border}`,
        boxShadow: shadow.md, padding: '32px 32px 28px', width: '100%', maxWidth: 400,
      }}>
        <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, letterSpacing: '-0.02em', marginBottom: 8 }}>
          Forgot password
        </div>

        {success ? (
          <div style={{
            background: colors.successLight, color: colors.success,
            border: `1px solid ${colors.successBorder}`, borderRadius: radius.md,
            padding: '12px 14px', fontSize: fontSize.sm, marginTop: 16,
          }}>
            If an account exists for {email}, you will receive a password reset email shortly.
          </div>
        ) : (
          <>
            <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: 24 }}>
              Enter your email and we'll send you a reset link.
            </div>

            {error && (
              <div style={{
                background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`,
                borderRadius: radius.md, padding: '10px 14px', fontSize: fontSize.sm, marginBottom: 16,
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 500, color: colors.text, marginBottom: 5, letterSpacing: '-0.01em' }}>
                  Email
                </label>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  style={{
                    width: '100%', height: componentHeight.inputMd, padding: '0 12px',
                    border: `1px solid ${colors.border}`, borderRadius: radius.md,
                    fontSize: fontSize.sm, color: colors.text, background: colors.surface,
                    boxSizing: 'border-box', outline: 'none', fontFamily: fontFamily.sans,
                    transition: `border-color ${transition.fast}`,
                  }}
                  required autoFocus placeholder="you@company.com"
                />
              </div>
              {turnstile.enabled && turnstile.siteKey && (
                <TurnstileWidget siteKey={turnstile.siteKey} onToken={setTurnstileToken} />
              )}
              <button
                type="submit" disabled={loading || (turnstile.enabled && !turnstileToken)}
                style={{
                  width: '100%', height: componentHeight.btnLg, background: colors.primary,
                  color: '#fff', border: 'none', borderRadius: radius.full, fontSize: fontSize.sm,
                  fontWeight: 500, cursor: 'pointer', marginTop: 8, fontFamily: fontFamily.sans,
                  transition: `background ${transition.fast}`,
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = colors.primaryHover }}
                onMouseLeave={e => { e.currentTarget.style.background = colors.primary }}
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
          </>
        )}

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: fontSize.sm, color: colors.textSecondary }}>
          <Link to="/login" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 500 }}>
            ← Back to sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
